import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { initItemsInvoiceParams, UkrPoshtaService } from '../../services/ukr-poshta.service';
import { IParams } from '../../model/http';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { AppTableSource, ColumnSortType } from '../../model/table.model';
import { NotificationService } from '../../services/notifications.service';
import { IntegrationService } from '../../services/integration.service';
import { BaseService } from '../../services/base.service';
import { tableHeaders } from './ukr-tab-tracker-invoice.interface';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '../../services/modal.service';
import { DialogService } from '../../services/dialog.service';
import { getUkrPoshtaStatus } from '../../helpers';
import { CreateTrackInvoiceComponent } from '../../dialogs/create-track-invoice/create-track-invoice.component';
import { activeTranslations, LOGISTIC_TYPES } from '../../enum/constants';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
  selector: 'app-ukr-tab-tracker-invoice',
  templateUrl: './ukr-tab-tracker-invoice.component.html',
  styleUrls: ['./ukr-tab-tracker-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UkrTabTrackerInvoiceComponent implements OnInit, OnDestroy {
  visibleColumns = tableHeaders.map((v) => v.value);
  public isLoader: boolean = false;
  private readonly destroy$: Subject<null> = new Subject<null>();
  selectionTable = new SelectionModel<any>(true, []);

  // Variables
  private itemsRequest: any;
  private dataSort: any;
  dataSource$: Observable<AppTableSource> = this.ukrPoshtaService.itemsInvoices$.pipe(
    map((data) => {
      tableHeaders.map((column) => {
        column.sort = ColumnSortType.NONE;
        if (column.value === this.dataSort?.active) {
          column.sort = this.dataSort.direction;
        }
        return column;
      });
      return {
        headers: tableHeaders,
        rows: data.items.map((v: any) => {
          return {
            ...v,
            status: v?.status,
            ukr_status: getUkrPoshtaStatus(v?.status),
          };
        }),
        pageSize: localStorage?.getItem('channelTake') || data?.size || 0,
        pageIndex: data.page ? data.page - 1 : 0,
        length: data.total || 0,
      };
    }),
  );
  private timerSearch: any;

  bulkActionList: any[] = [{ label: 'delete', value: 'DELETE' }];

  constructor(
    private readonly notificationService: NotificationService,
    private readonly integrationService: IntegrationService,
    private readonly ukrPoshtaService: UkrPoshtaService,
    public readonly baseService: BaseService,
    public readonly dialog: MatDialog,
    public readonly modalService: ModalService,
    private readonly dialogService: DialogService,
  ) {}

  getItemsWithUpdatedParams(params: IParams, isLoader = false) {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }

    this.isLoader = isLoader;
    this.selectionTable.clear();
    this.itemsRequest = this.ukrPoshtaService
      .setQueryListInvoicesParams(params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (res) => {
          this.isLoader = false;
          if (!res.items.length && res.page > 1) {
            this.getItemsWithUpdatedParams(
              {
                page: res.page - 1,
                take: res.take,
              },
              true,
            );
          }
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnInit() {
    this.isLoader = true;
    this.ukrPoshtaService
      .loadListInvoices()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.isLoader = false;
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onSortChange(sortState: any) {
    this.dataSort = sortState;
    this.getItemsWithUpdatedParams({
      sort: sortState.active,
      order: sortState.direction,
    });
  }

  onPaginationChange(paginationDetails: any) {
    const { pageIndex, pageSize } = paginationDetails;
    localStorage.removeItem('invoiceTake');
    localStorage.setItem('invoiceTake', pageSize);
    this.ukrPoshtaService.updateInvoiceTake();

    const page = pageIndex + 1;
    const take = pageSize;
    this.getItemsWithUpdatedParams({ page, take }, true);
  }

  onActionChange(event: any) {
    if (event.label === 'DELETE') {
      const dialogRef = this.dialogService.openConfirm({
        text: 'dialogs.ttn_delete',
      });

      dialogRef.subscribe((result) => {
        if (result) {
          this.ukrPoshtaService
            .deleteInvoice(event.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.getItemsWithUpdatedParams({});
              },
            });
        }
      });
    }

    if (event.label === 'EDIT') {
      const dialogData = {
        title: 'update',
        translate: true,
        component: CreateTrackInvoiceComponent,
        width: '30vw',
        data: {
          is_update: true,
          id: event.id,
          ttn: event.ttn,
          phone: event.phone,
          comments: event.comments,
          type: LOGISTIC_TYPES.UKR_POSHTA,
        },
      };

      this.modalService.openModal(dialogData).subscribe((result) => {
        if (result) {
          this.getItemsWithUpdatedParams({});
        }
      });
    }
  }

  onSearchChange(search: string) {
    if (this.timerSearch) {
      clearTimeout(this.timerSearch);
    }

    this.timerSearch = setTimeout(() => {
      this.getItemsWithUpdatedParams(
        {
          ...initItemsInvoiceParams,
          search,
        },
        true,
      );
    }, 500);
  }

  onAdd() {
    const dialogData = {
      title: 'add_ttn',
      translate: true,
      component: CreateTrackInvoiceComponent,
      width: '30vw',
      data: {
        type: LOGISTIC_TYPES.UKR_POSHTA,
      },
    };

    this.modalService.openModal(dialogData).subscribe((result) => {
      if (result) {
        this.getItemsWithUpdatedParams({
          ...initItemsInvoiceParams,
        });
      }
    });
  }
  bulkActionPopup(event: any) {
    if (event.value === 'DELETE') {
      const ids = this.selectionTable.selected.map((v) => v.id);
      this.ukrPoshtaService.deleteBulk(ids).subscribe({
        next: (res) => {
          if (res?.length) {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_deleted,
            );
            this.getItemsWithUpdatedParams({});
          }
        },
        error: (res) => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_while_delete_2,
          );
        },
      });
    }
  }
}
